import React, { useMemo, useState } from 'react';
import { compose } from 'redux';
import { View, ScrollView, RefreshControl } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import sortServiceDockets from '../../../common/utils/serviceDocket/sortServiceDockets';
import { formatDate } from '../../../common/utils/utils';

import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useRefetchOnFocus } from '../../hooks';
import { Dialog } from '../../components';
import Text from '../../components/text/Text';
import { Card } from '../../design/components';
import { DetailContainer } from '../../containers';

import { ServiceRunPicklistDetail, ServiceRunWarehouseDetail, ServiceRunVehicleDetail } from './ServiceRunDetail/index';
import ServiceRunServicingDetail from './ServiceRunDetail/ServiceRunServicingDetail';
import ServiceRunDetailMachineListView from './ServiceRunDetailMachineListView';
import ServiceRunCashBags from './ServiceRunCashBags';

const ServiceRunDetail = ({ refetch, data: servicerun }) => {

	const { serviceDockets } = servicerun;
	useRefetchOnFocus(refetch);

	const [isServiceRunCashBagsDialogOpen, setIsServiceRunCashBagsDialogOpen] = useState(false);

	const [refreshPage, isRefreshing] = useRefreshPage(refetch);

	// Sort dockets by service time if completed, and by location if not
	const dockets = useMemo(() => [...serviceDockets].sort(sortServiceDockets), [serviceDockets]);

	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();

	return (
		<>
			<ScrollView
				style={styles.scrollView}
				contentContainerStyle={styles.scrollContent}
				scrollIndicatorInsets={{ right: 1 }}
				refreshControl={(
					<RefreshControl
						refreshing={isRefreshing}
						onRefresh={refreshPage}
					/>
				)}
			>
				<View style={styles.headingBlock}>
					<Text style={styles.heading} accessibilityRole="header">
						{(servicerun.route && servicerun.route.name) ? servicerun.route.name : 'Routeless service run'}
					</Text>
					<View style={styles.subheadingRow}>
						<Feather name="calendar" size={16} color={colors.iconMuted} />
						<Text style={styles.subheading}>
							{servicerun.planned
								? `${formatDate(servicerun.planned, 'DAY_MONTHWORD_YEAR')}`
								: 'No date'}
						</Text>
					</View>
				</View>
				{/* Same raised surface as the machine cards below it. */}
				<Card tone="raised" style={styles.detailCard}>
					<ServiceRunVehicleDetail servicerun={servicerun} styles={styles} />

					<ServiceRunWarehouseDetail servicerun={servicerun} styles={styles} />

					<ServiceRunServicingDetail
						servicerun={servicerun}
						styles={styles}
						setIsServiceRunCashBagsDialogOpen={setIsServiceRunCashBagsDialogOpen}
					/>
					<ServiceRunPicklistDetail servicerun={servicerun} styles={styles} refetch={refetch} />
				</Card>
				<ServiceRunDetailMachineListView
					items={dockets}
				/>
			</ScrollView>
			<Dialog
				isOpen={isServiceRunCashBagsDialogOpen}
				onClose={() => setIsServiceRunCashBagsDialogOpen(false)}
			>
				<ServiceRunCashBags
					dockets={dockets}
					serviceRun={servicerun}
				/>
			</Dialog>
		</>
	);
};

// Section components (including ServiceRunVehicleDetail) receive these styles
// and read headerText, detailView, nthRow, firstButton, secondButton,
// leftItemWidth and separator from them.
const createStyles = ({ ui: { colors, type, space } }) => ({
	scrollView: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	scrollContent: {
		paddingTop: 20,
		paddingBottom: space.lg,
	},
	headingBlock: {
		paddingHorizontal: space.gutter,
		gap: 6,
		marginBottom: space.gutter,
	},
	heading: {
		...type.title,
		color: colors.text,
	},
	subheadingRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
	},
	subheading: {
		...type.bodySmall,
		color: colors.textSecondary,
		flexShrink: 1,
	},
	detailCard: {
		marginHorizontal: space.gutter,
		marginBottom: space.md,
		paddingTop: space.sm,
	},
	firstButton: {
		flex: 1,
		marginRight: space.xs,
	},
	secondButton: {
		flex: 1,
		marginLeft: space.xs,
	},
	leftItemWidth: {
		minWidth: '47%',
		paddingRight: space.sm,
	},
	nthRow: {
		marginTop: space.sm,
		flexDirection: 'row',
	},
	headerText: {
		...type.sectionTitle,
		marginTop: space.xs,
		color: colors.text,
	},
	detailView: {
		flexDirection: 'column',
		marginBottom: space.md,
		marginTop: space.xs,
	},
	separator: {
		marginLeft: -space.lg,
		marginRight: -space.lg,
		marginTop: 0,
		marginBottom: space.md,
	},
});


export default compose(DetailContainer)(ServiceRunDetail);
